import { useEffect, useState, useRef } from "react";

import DraftJS from "draft-js";
const { convertToRaw, ContentState, EditorState } = DraftJS;

import draftToHtml from "draftjs-to-html";

import { MarkdownEditorProps } from "./MarkdownEditor.types";
import MarkdownEditor from "./MarkdownEditor";

const addTypographyDataAttributes = (html: string) => {
  const doc = new DOMParser().parseFromString(html, "text/html");

  doc
    .querySelectorAll("p")
    .forEach((el) => el.setAttribute("data-typography", "body"));
  doc
    .querySelectorAll("h1")
    .forEach((el) => el.setAttribute("data-typography", "h1"));
  doc
    .querySelectorAll("h2")
    .forEach((el) => el.setAttribute("data-typography", "h2"));
  doc
    .querySelectorAll("ul li, ol li")
    .forEach((el) => el.setAttribute("data-typography", "body"));

  return doc.body.innerHTML;
};

function isBrowser() {
  return typeof window !== "undefined";
}

const MarkdownEditorContainer = ({
  className,
  value,
  onChange,
}: MarkdownEditorProps) => {
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty(),
  );
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!isBrowser()) return;

    if (initializedRef.current) return;

    let cancelled = false;

    (async () => {
      try {
        const mod = await import("html-to-draftjs");
        const htmlToDraft = (mod as any).default ?? mod;

        const contentBlock = htmlToDraft(value ?? "");
        const contentState = ContentState.createFromBlockArray(
          contentBlock.contentBlocks,
        );

        if (!cancelled) {
          setEditorState(EditorState.createWithContent(contentState));
          initializedRef.current = true;
        }
      } catch (error) {
        console.error("Error initializing editor state:", error);

        if (!cancelled) {
          setEditorState(EditorState.createEmpty());
          initializedRef.current = true;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [value]);

  const handleChange = (newEditorState: any) => {
    setEditorState(newEditorState);

    const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
    const updatedHtml: any = addTypographyDataAttributes(html);

    onChange(updatedHtml);
  };

  return (
    <MarkdownEditor
      className={className}
      value={editorState}
      onChange={handleChange}
    />
  );
};

export default MarkdownEditorContainer;
